import { FunctionComponent, useState } from 'react';
import {
    Box,
    Link,
    Stack,
    SvgIcon,
    Tab,
    Typography,
    useTheme,
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { useAuthentication } from '@/hooks/useAuthentication';
import {
    useGetConditionLinkQuery,
    useGetImportedConditionLinkQuery,
} from '@/api/v2/conditionLinks';
import ArrowLeftIcon from '@heroicons/react/24/solid/ArrowLeftIcon';
import {
    ConditionLink,
    ImportedConditionLink,
    isOfTypeOriginalConditionLink,
} from '@/models/conditionLink';
import { ConditionLinkGeneralData } from '@/pages/rulesets/components/CondtionLinkGeneralData';
import { ContingentConditionConfiguration } from '@/pages/rulesets/components/conditions/ContingentConditionConfiguration';
import { PriceConditionConfiguration } from '@/pages/rulesets/components/conditions/PriceConditionConfiguration';

type TabValue = 'general' | 'priceCondition' | 'contingentCondition';

type EditConditionLinkContentProps = {
    id: string;
    importId?: string;
};

export const EditConditionLinkContent: FunctionComponent<
    EditConditionLinkContentProps
> = ({ id, importId }) => {
    const theme = useTheme();
    const [tabValue, setTabValue] = useState<TabValue>('general');
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const [, setConditionLink] = useState<
        ConditionLink | ImportedConditionLink
    >(null);

    const { data: conditionLink } = useGetConditionLinkQuery(
        {
            liftOperatorId,
            id: id,
        },
        {
            skip: !!importId,
        }
    );
    const { data: importedConditionLink } = useGetImportedConditionLinkQuery(
        {
            liftOperatorId,
            id: importId,
        },
        {
            skip: !importId,
        }
    );

    const handleTabChange = (_, newValue: TabValue) => {
        setTabValue(newValue);
    };

    if (!conditionLink && !importedConditionLink) return null;

    const linkToRender = id ? conditionLink : importedConditionLink;

    return (
        <TabContext value={tabValue}>
            <Link
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: 'text.primary',
                    mb: 6,
                }}
                href='/regelwerke'
            >
                <SvgIcon
                    fontSize='small'
                    sx={{
                        mr: 2,
                    }}
                >
                    <ArrowLeftIcon />
                </SvgIcon>

                <Typography variant='subtitle2' component='span'>
                    Regelwerke
                </Typography>
            </Link>

            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                {isOfTypeOriginalConditionLink(linkToRender)
                    ? 'Regelwerk'
                    : 'Importiertes Regelwerk'}{' '}
                <Typography
                    sx={{
                        color: 'primary.main',
                    }}
                    variant='h4'
                    component='span'
                >
                    {linkToRender?.name}
                </Typography>{' '}
                bearbeiten
            </Typography>

            <Stack spacing={theme.page.verticalSpacing}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <TabList onChange={handleTabChange}>
                        <Tab label='Allgemeine Konfiguration' value='general' />
                        <Tab
                            label='Regelwerk Verfügbarkeit'
                            value='contingentCondition'
                            disabled={
                                !isOfTypeOriginalConditionLink(linkToRender)
                            }
                        />
                        <Tab
                            label='Regelwerk Preis'
                            value='priceCondition'
                            disabled={
                                !isOfTypeOriginalConditionLink(linkToRender)
                            }
                        />
                    </TabList>
                </Box>

                {!!linkToRender && (
                    <>
                        <TabPanel value='general'>
                            <ConditionLinkGeneralData
                                conditionLink={linkToRender}
                            />
                        </TabPanel>

                        {isOfTypeOriginalConditionLink(linkToRender) && (
                            <>
                                <TabPanel value='contingentCondition'>
                                    <ContingentConditionConfiguration
                                        conditionLink={linkToRender}
                                        onUpdateConditionLink={(
                                            linkToRender
                                        ) => {
                                            setConditionLink(linkToRender);
                                        }}
                                    />
                                </TabPanel>

                                <TabPanel value='priceCondition'>
                                    <PriceConditionConfiguration
                                        conditionLink={linkToRender}
                                        onUpdateConditionLink={(
                                            conditionLink
                                        ) => {
                                            setConditionLink(conditionLink);
                                        }}
                                    />
                                </TabPanel>
                            </>
                        )}
                    </>
                )}
            </Stack>
        </TabContext>
    );
};
